import {clearBodyLocks, lock, unlock} from 'tua-body-scroll-lock';
import Dialog from './Dialog';
import Events from './Events';

/**
 * Overlay class
 */
export default class Manager
{
    /**
     * Set up details
     */
    constructor()
    {
        this.bind();
        this.stack = [];
        this.dialogs = [];
        this.debug = false;
    }


    /**
     * Handle global bindings
     */
    bind()
    {
        document.addEventListener('keydown', (e) => {
            if (this.current) {
                var focusedElements = this.current.getFocusableElements();
                if (focusedElements.length) {
                    var firstFocusableEl = focusedElements[0];
                    var lastFocusableEl = focusedElements[focusedElements.length - 1];

                    if (e.key === 'Tab') {
                        if (e.shiftKey && document.activeElement === firstFocusableEl) {
                            e.preventDefault();
                            lastFocusableEl.focus();
                        } else if (!e.shiftKey && document.activeElement === lastFocusableEl) {
                            e.preventDefault();
                            firstFocusableEl.focus();
                        } else if (!document.activeElement.closest('.dialog')) {
                            e.preventDefault();
                            firstFocusableEl.focus();
                        }
                    }
                }
            }
        });
    }

    /**
     * Merge config options, converts a partial config into a complete one
     *
     * @param {object} options
     * @returns object
     */
    parseConfig(options) {
        var config = {
            "method": 'GET',
            "script_callback": () => {},
            "wait_for_scripts": false,
            "data": null,
            "modifiers": [],
            "response_type": 'json',
            "callback": () => {},
            "close": () => {},
            "closed": () => {},
            "cross_transition": false,
            "stack": false,
            "loading": 'background',
            "loading_template": null,
            "loading_content": null,
            "transition": 'fade',
            "eval_scripts": false,
            "append_to": "#outer-wrapper",
            "loader_template": '#dialog-loader',
            "close_template": '#dialog-close',
            "scrollable_element": null,
        };

        if (options) {
            for (var prop in options) {
                if (config.hasOwnProperty(prop)) {
                    config[prop] = options[prop];
                }
            }
        }

        return config;
    }

    /**
     * Render the overlay with content from a callabale or passed as a selector
     *
     * @param {string|callable} selector
     * @param {object} options
     */
    render(selector, options) {
        var config = this.parseConfig(options);

        const element = document.querySelector(selector);

        if (element && element.tagName === 'DIALOG') {
            const id = element.getAttribute('id');
            if (!id) {
                console.error('Dialog element must have an id attribute');
                return;
            }

            let dialog = this.getExistingDialog(element);
            this.applyTemplate(element, config);

            if (dialog == null) {
                dialog = new Dialog(element);
                dialog.addEventListener(Events.OPEN, this.open.bind(this));
                dialog.addEventListener(Events.CLOSED, this.closed.bind(this));
                this.dialogs.push(dialog);
                this.log('Creating new dialog', dialog );
            }

            if (config.stack || !this.current) {
                this.stack.push(dialog);
                dialog.open(config);
            } else {
                this.current.addEventListener(Events.CLOSED, (e) => {
                    this.stack.push(dialog);
                    dialog.open(config);
                }, { once: true });
                if (config.cross_transition) {
                    this.current.cross = true;
                    this.current.close();
                } else {
                    this.current.close();
                }
            }

            return dialog;
        } else {
            console.error('Invalid selector or dialog element not found: ' + selector);
            return null;
        }
    }

    /**
     * Load content via ajax
     *
     * @param {string} url
     * @param {object} options
     */
    load(url, options) {
        if (!this.isUrlRelative(url)) {
            console.error('Invalid URL: ' + url);
            return;
        }

        var config = this.parseConfig(options);
        config.url = url;
        const currentDialog = this.current;

        /** @type {Dialog} */
        let targetDialog = null;

        // If we have a current dialog and it is dynamic, we load the content into it unless we are stacking
        // We don't want to load into a non-dynamic dialog as we will trash it's content
        if (!config.stack && currentDialog && currentDialog.isDynamic() && !config.cross_transition) {
            targetDialog = currentDialog;
            this.applyTemplate(targetDialog.target, config);
        } else {
            const dialogTemplate = this.createDialog(config);
            const dialog = new Dialog(dialogTemplate);
            dialog.addEventListener(Events.OPEN, this.open.bind(this));
            dialog.addEventListener(Events.CLOSED, this.closed.bind(this));

            targetDialog = dialog;
        }

        const pushTargetDialog = () => {
            if (!this.stack.includes(targetDialog)) {
                this.stack.push(targetDialog);
            }
        };

        if (config.stack || !config.cross_transition || !currentDialog) {
            pushTargetDialog();
            targetDialog.load(url, config);
        } else {
            currentDialog.addEventListener(Events.CLOSED, (e) => {
                pushTargetDialog();
                targetDialog.load(url, config);
            }, { once: true });

            if (config.cross_transition) {
                currentDialog.cross = true;
                currentDialog.close();
            } else {
                currentDialog.close();
            }
        }

        return targetDialog;
    }

    close(callback) {
        if (this.current) {
            this.current.addEventListener(Events.CLOSED, (e) => {
                if (callback) {
                    callback.call(this);
                }
            }, { once: true });
            this.current.close();
        } else {
            console.warn('No current dialog to close');
        }
    }

    /**
     * See if the URL is relative for security purposes we don't want to load
     * external URL's. Absolute urls on the same domain are also cool
     *
     * @param {string} url
     * @returns {bool}
     */
    isUrlRelative(url)
    {
        if (url.indexOf(window.location.protocol + '//' + window.location.host) === 0) {
            return true;
        }

        if (url.indexOf('//' + window.location.host) === 0) {
            return true;
        }

        const r = new RegExp('^(?:[a-z+]+:)?//', 'i');

        return !r.test(url);
    }

    open(e) {
        if (!this.oldFocus) {
            this.oldFocus = document.activeElement;
        }

        lock(this.current.scrollableElement);
    }

    closed(e) {
        this.log('closed', e.target);
        for (let index = 0; index < this.stack.length; index++) {
            const element = this.stack[index];
            if (element === e.target) {
                this.stack.splice(index, 1);
                unlock(element.scrollableElement);
                break;
            }
        }

        if (e.target.isDynamic()) {
            this.log('Destroying dynamic dialog', e.target);
            e.target.destroy();
        }

        if (this.stack.length === 0) {
            this.log('No more dialogs in stack, unlocking body scroll');
            clearBodyLocks();

            if (this.oldFocus && document.activeElement !== this.oldFocus) {
                this.oldFocus.focus();
            }
            this.oldFocus = null;
        }
    }

    getExistingDialog(element) {
        for (let index = 0; index < this.dialogs.length; index++) {
            const dialog = this.dialogs[index];
            if (dialog.target === element) {
                return dialog;
            }
        }
        return null;
    }

    createDialog(config)
    {
        const dialog = document.createElement('dialog');
        dialog.classList.add('dialog');
        dialog.setAttribute('dynamic', '');
        dialog.setAttribute('closedby', 'any');
        this.applyTemplate(dialog, config);
        this.applyLoadingTemplate(dialog, config);
        document.querySelector(config.append_to || '#outer-wrapper').appendChild(dialog);

        return dialog;
    }

    applyTemplate(dialog, config)
    {
        if (!dialog.classList.contains('dialog')) {
            dialog.classList.add('dialog');
        }

        let content = dialog.querySelector('.dialog__content');
        let controls = dialog.querySelector('.dialog__controls');

        if (!content) {
            const nodes = [];
            const children = Array.from(dialog.children);
            for (let i = 0; i < children.length; i++) {
                const node = children[i];
                dialog.removeChild(node);
                nodes.push(node);
            }

            controls = document.createElement('div');
            controls.classList.add('dialog__controls');
            dialog.appendChild(controls);

            content = document.createElement('div');
            content.classList.add('dialog__content');
            dialog.appendChild(content);

            for (let index = 0; index < nodes.length; index++) {
                const element = nodes[index];
                content.appendChild(element);
            }
        }

        // Reset controls
        controls.innerHTML = '';
        this.applyCloseTemplate(dialog, config);
    }

    applyCloseTemplate(dialog, config)
    {
        const controls = dialog.querySelector('.dialog__controls');
        const template = document.querySelector(
            config && config.close_template ? config.close_template : '#dialog-close'
        );

        if (template) {
            const templateContent = template.content.cloneNode(true);
            controls.appendChild(templateContent);
        } else {
            const templateContent = document.createElement('template');
            templateContent.innerHTML = `
<button class="dialog__close" dialog-close aria-label="Close" type="button">
    <svg class="dialog__close-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="50" height="50">
        <path d="m0 0 50 50M50 0 0 50"/>
    </svg>
</button>
`;
            controls.appendChild(templateContent.content.cloneNode(true));
        }
    }

    applyLoadingTemplate(dialog, config) {
        const controls = dialog.querySelector('.dialog__controls');
        const template = document.querySelector(
            config && config.loader_template ? config.loader_template : '#dialog-loader'
        );

        if (template) {
            controls.appendChild(template.content.cloneNode(true));
        } else {
            const templateContent = document.createElement('template');
            templateContent.innerHTML = `<div class="dialog__loader"></div>`;
            controls.appendChild(templateContent.content.cloneNode(true));
        }
    }

    log(message, ...extra) {
        if (this.debug) {
            console.log('Dialog Manager:', message, ...extra);
        }
    }

    get current() {
        if (this.stack.length) {
            return this.stack[this.stack.length - 1];
        }

        return null;
    }
}
